/**
 * 引用工具提示组件
 *
 * 为消息中的设定引用(@{setting_name})和章节引用(#{chapter_title})显示详细信息
 *
 * 功能:
 * - 鼠标悬停在引用标记上时显示工具提示
 * - 显示设定详情(名称、分类、内容预览)
 * - 显示章节详情(标题、字数、摘要)
 * - 使用平台配色方案
 */

import React, { useState, useCallback } from 'react';

// ==================== 类型定义 ====================

/** 引用类型 */
export type ReferenceType = 'setting' | 'chapter';

/** 引用数据接口 */
export interface ReferenceData {
  /** 引用类型 */
  type: ReferenceType;
  /** 引用名称/标题 */
  name: string;
  /** 分类(仅设定) */
  category?: string;
  /** 内容预览 */
  preview?: string;
  /** 字数(仅章节) */
  wordCount?: number;
  /** 摘要(仅章节) */
  summary?: string;
}

// ==================== 组件接口 ====================

interface ReferenceTooltipProps {
  /** 引用数据 */
  reference: ReferenceData;
  /** 子元素(被引用的文本) */
  children: React.ReactElement;
}

// ==================== 工具函数 ====================

/**
 * 截断文本到指定长度
 */
function truncateText(text: string, maxLength: number): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * 格式化数字(添加千位分隔符)
 */
function formatNumber(num: number): string {
  return num.toLocaleString('zh-CN');
}

// ==================== 组件实现 ====================

/**
 * 引用工具提示组件
 *
 * 使用绝对定位的tooltip显示引用的详细信息
 */
export const ReferenceTooltip: React.FC<ReferenceTooltipProps> = ({ reference, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // 处理鼠标进入，计算tooltip位置
  const handleMouseEnter = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8, // 在元素下方8px处显示
      left: rect.left
    });
    setIsVisible(true);
  }, []);

  // 处理鼠标离开
  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block cursor-help"
      >
        {children}
      </span>

      {/* Tooltip */}
      {isVisible && (
        <div
          className="fixed z-50 w-80 p-3 bg-white border border-gray-200 rounded-lg shadow-sm"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: 'translateX(-50%)'
          }}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          {/* 设定引用 */}
          {reference.type === 'setting' && (
            <div>
              {/* 引用类型标签 */}
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded">
                  设定引用
                </span>
                {reference.category && (
                  <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded">
                    {reference.category}
                  </span>
                )}
              </div>

              {/* 设定名称 */}
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {reference.name}
              </h4>

              {/* 内容预览 */}
              {reference.preview && (
                <p className="text-xs text-gray-600 leading-relaxed">
                  {truncateText(reference.preview, 150)}
                </p>
              )}
            </div>
          )}

          {/* 章节引用 */}
          {reference.type === 'chapter' && (
            <div>
              {/* 引用类型标签 */}
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded">
                  章节引用
                </span>
                {reference.wordCount !== undefined && (
                  <span className="px-2 py-0.5 bg-gray-50 text-gray-600 text-xs rounded">
                    {formatNumber(reference.wordCount)} 字
                  </span>
                )}
              </div>

              {/* 章节标题 */}
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {reference.name}
              </h4>

              {/* 摘要 */}
              {reference.summary && (
                <p className="text-xs text-gray-600 leading-relaxed">
                  {truncateText(reference.summary, 150)}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// ==================== 工具函数 ====================

/**
 * 解析消息内容中的引用标记
 *
 * @param content 消息内容(已经过HTML转义)
 * @param settings 设定列表(可选)
 * @param chapters 章节列表(可选)
 * @returns 解析后的React节点数组
 */
export function parseReferences(
  content: string,
  settings?: Array<{ name: string; category?: string; content?: string }>,
  chapters?: Array<{ title: string; wordCount?: number; summary?: string }>
): React.ReactNode {
  if (!content) return content;

  // 注意：content已经过HTML转义，所以正则需要匹配转义后的字符
  // @{...} 变成 @{...} (花括号不会被转义)
  const settingRegex = /@\{([^}]+)\}/g;
  const chapterRegex = /#\{([^}]+)\}/g;

  // 分割文本为片段
  const parts: Array<{
    type: 'text' | 'setting' | 'chapter';
    content: string;
  }> = [];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // 查找所有引用标记
  const allMatches: Array<{
    type: 'setting' | 'chapter';
    name: string;
    index: number;
    length: number;
  }> = [];

  // 匹配设定引用
  while ((match = settingRegex.exec(content)) !== null) {
    allMatches.push({
      type: 'setting',
      name: match[1],
      index: match.index,
      length: match[0].length
    });
  }

  // 重置regex
  settingRegex.lastIndex = 0;

  // 匹配章节引用
  while ((match = chapterRegex.exec(content)) !== null) {
    allMatches.push({
      type: 'chapter',
      name: match[1],
      index: match.index,
      length: match[0].length
    });
  }

  // 按索引排序
  allMatches.sort((a, b) => a.index - b.index);

  // 构建片段数组
  allMatches.forEach((match) => {
    // 添加引用前的文本
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: content.slice(lastIndex, match.index)
      });
    }

    // 添加引用
    parts.push({
      type: match.type,
      content: match.name
    });

    lastIndex = match.index + match.length;
  });

  // 添加最后的文本
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      content: content.slice(lastIndex)
    });
  }

  // 如果没有引用，返回原始内容
  if (parts.length === 0) {
    return content;
  }

  // 转换为React节点
  return (
    <>
      {parts.map((part, index) => {
        if (part.type === 'text') {
          return <span key={index}>{part.content}</span>;
        }

        const reference: ReferenceData = {
          type: part.type,
          name: part.content
        };

        // 查找引用的详细数据
        if (part.type === 'setting' && settings) {
          const setting = settings.find((s) => s.name === part.content);
          if (setting) {
            reference.category = setting.category;
            reference.preview = setting.content;
          }
        } else if (part.type === 'chapter' && chapters) {
          const chapter = chapters.find((c) => c.title === part.content);
          if (chapter) {
            reference.wordCount = chapter.wordCount;
            reference.summary = chapter.summary;
          }
        }

        // 引用标记的样式
        const markerClass = 'text-accent-600 font-medium bg-accent-50 px-1 rounded cursor-help';

        return (
          <ReferenceTooltip key={index} reference={reference}>
            <span className={markerClass}>
              {part.type === 'setting' ? '@' : '#'}
              {part.content}
            </span>
          </ReferenceTooltip>
        );
      })}
    </>
  );
}
